/* ==========================================================================
   W1 REGEN ULTRA — section behaviour
   Port of the inline <script> in preview/w1-regen-ultra-preview.html.

   Inline onclick handlers are replaced with delegated, per-root listeners so
   each block can be re-initialised on shopify:section:load in the editor.
   ========================================================================== */
(function () {
  'use strict';

  // Every section pulls in this file, so the tag appears once per section on the
  // page and the browser executes each copy. Bail out after the first.
  if (window.W1RegenUltra) return;

  var INIT_FLAG = 'w1ruInit';

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function markInit(el) {
    if (el.dataset[INIT_FLAG] === 'true') return false;
    el.dataset[INIT_FLAG] = 'true';
    return true;
  }

  function setActive(list, activeEl, className) {
    each(list, function (el) {
      el.classList.toggle(className, el === activeEl);
    });
  }

  /* ----------------------------------------------------------------------
     Media gallery
     ---------------------------------------------------------------------- */
  function initGallery(root) {
    if (!markInit(root)) return;

    var mainImg = root.querySelector('[data-w1ru-gallery-main]');
    var counter = root.querySelector('[data-w1ru-gallery-counter]');
    var strip = root.querySelector('[data-w1ru-thumbs]');
    var stripWrap = root.querySelector('[data-w1ru-thumbs-wrap]');
    var stripPrev = root.querySelector('[data-w1ru-thumbs-prev]');
    var stripNext = root.querySelector('[data-w1ru-thumbs-next]');
    var thumbs = root.querySelectorAll('[data-w1ru-thumb]');
    if (!mainImg || !thumbs.length) return;

    var index = 0;

    // The strip shows six thumbnails and scrolls for the rest, so the one that
    // matches the main image has to be brought along when the arrows or the
    // variant cards change it.
    function revealThumb(thumb) {
      if (!strip || strip.scrollWidth <= strip.clientWidth) return;
      var stripBox = strip.getBoundingClientRect();
      var thumbBox = thumb.getBoundingClientRect();
      var offset = thumbBox.left - stripBox.left - (stripBox.width - thumbBox.width) / 2;
      strip.scrollLeft += offset;
    }

    // Hiding the scrollbar also hid the only sign that the strip moves. Mark
    // each direction that still has thumbnails behind it so the stylesheet can
    // fade that edge, and show the matching arrow.
    function syncStripEdges() {
      if (!strip || !stripWrap) return;
      var slack = strip.scrollWidth - strip.clientWidth;
      // Fractional widths leave a sub-pixel of travel at either end.
      var before = slack > 1 && strip.scrollLeft > 1;
      var after = slack > 1 && strip.scrollLeft < slack - 1;
      stripWrap.classList.toggle('has-more-before', before);
      stripWrap.classList.toggle('has-more-after', after);
      if (stripPrev) stripPrev.hidden = !before;
      if (stripNext) stripNext.hidden = !after;
    }

    var edgeSyncPending = false;

    // Smooth scrolling fires this on every frame of the animation.
    function queueStripSync() {
      if (edgeSyncPending) return;
      edgeSyncPending = true;
      requestAnimationFrame(function () {
        edgeSyncPending = false;
        syncStripEdges();
      });
    }

    // Move by whole thumbnails so the row never comes to rest mid-image.
    function pageStrip(direction) {
      if (!strip) return;
      var gap = parseFloat(window.getComputedStyle(strip).columnGap) || 0;
      var step = thumbs[0].getBoundingClientRect().width + gap;
      if (!step) return;
      var perView = Math.max(1, Math.round((strip.clientWidth + gap) / step));
      strip.scrollLeft += direction * perView * step;
    }

    function show(next) {
      index = (next + thumbs.length) % thumbs.length;
      var thumb = thumbs[index];
      var src = thumb.getAttribute('data-src');
      var srcset = thumb.getAttribute('data-srcset');
      var alt = thumb.getAttribute('data-alt') || '';

      if (src) mainImg.setAttribute('src', src);
      if (srcset) mainImg.setAttribute('srcset', srcset);
      else mainImg.removeAttribute('srcset');
      mainImg.setAttribute('alt', alt);

      setActive(thumbs, thumb, 'is-active');
      each(thumbs, function (el) {
        el.setAttribute('aria-selected', el === thumb ? 'true' : 'false');
      });
      revealThumb(thumb);
      queueStripSync();

      if (counter) counter.textContent = index + 1 + ' / ' + thumbs.length;
    }

    each(thumbs, function (thumb, i) {
      thumb.addEventListener('click', function () {
        show(i);
      });
    });

    var prev = root.querySelector('[data-w1ru-gallery-prev]');
    var next = root.querySelector('[data-w1ru-gallery-next]');
    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    if (strip) {
      strip.addEventListener('scroll', queueStripSync);
      if (stripPrev) stripPrev.addEventListener('click', function () { pageStrip(-1); });
      if (stripNext) stripNext.addEventListener('click', function () { pageStrip(1); });

      // Whether the row overflows depends on its width, and the columns are a
      // percentage of it, so watching the strip covers the thumbnails too.
      if (window.ResizeObserver) {
        new ResizeObserver(queueStripSync).observe(strip);
      } else {
        window.addEventListener('resize', queueStripSync);
      }
    }

    root.w1ruShowMedia = function (mediaId) {
      if (!mediaId) return;
      for (var i = 0; i < thumbs.length; i++) {
        if (thumbs[i].getAttribute('data-media-id') === String(mediaId)) {
          show(i);
          return;
        }
      }
    };

    show(0);
  }

  /* ----------------------------------------------------------------------
     Sticky purchase bar — the buy box's second face, once it has scrolled off
     ---------------------------------------------------------------------- */
  function initStickyBar(root) {
    var bar = root.querySelector('[data-w1ru-stickybar]');
    var anchor = root.querySelector('[data-w1ru-sticky-anchor]');
    if (!bar || !anchor) return null;

    // Only once the real Add to cart button has left upwards. While that button
    // is on screen the bar would be a second copy of a control already in view,
    // and above the buy box it would cover the page for nothing.
    //
    // Read on scroll rather than through an IntersectionObserver: an observer
    // only reports a crossing, and a jump — an anchor link, a restored scroll
    // position — can put the button from below the fold to above it without
    // ever passing through, leaving the bar behind. One rect per frame is
    // cheaper than being wrong.
    var showing = null;
    function syncVisibility() {
      var scrolledPast = anchor.getBoundingClientRect().top < 0;
      if (scrolledPast === showing) return;
      showing = scrolledPast;
      bar.classList.toggle('is-visible', scrolledPast);
      bar.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
    }

    var framePending = false;
    function queueVisibility() {
      if (framePending) return;
      framePending = true;
      requestAnimationFrame(function () {
        framePending = false;
        syncVisibility();
      });
    }

    window.addEventListener('scroll', queueVisibility, { passive: true });
    window.addEventListener('resize', queueVisibility);
    syncVisibility();

    var priceEl = bar.querySelector('[data-w1ru-sticky-price]');
    var ctaEl = bar.querySelector('[data-w1ru-sticky-cta]');
    // Absent when the editor has pinned that line to a fixed string.
    var titleEl = bar.querySelector('[data-w1ru-sticky-title]');
    var subEl = bar.querySelector('[data-w1ru-sticky-sub]');
    var scratch = document.createElement('div');

    // The included copy is rich text that opens with its own heading in a
    // <strong>. The bar has just said the version's name, so drop it and keep
    // the list, as text rather than markup.
    function includedList(html) {
      scratch.innerHTML = html || '';
      var lead = scratch.querySelector('strong');
      if (lead) lead.remove();
      return scratch.textContent.replace(/\s+/g, ' ').trim();
    }

    return function syncStickyBar(button, labels) {
      if (priceEl) priceEl.innerHTML = button.getAttribute('data-price') || priceEl.innerHTML;
      if (titleEl) titleEl.textContent = button.getAttribute('data-variant-title') || titleEl.textContent;
      if (subEl) {
        var list = includedList(button.getAttribute('data-included'));
        subEl.textContent = list;
        subEl.hidden = list === '';
      }
      if (!ctaEl) return;
      ctaEl.disabled = !labels.available;
      if (ctaEl.hasAttribute('data-w1ru-sticky-cta-follows')) ctaEl.textContent = labels.text;
    };
  }

  /* ----------------------------------------------------------------------
     Buy box — variant cards, quantity, price
     ---------------------------------------------------------------------- */
  function initBuybox(root) {
    if (!markInit(root)) return;

    var variantInput = root.querySelector('[data-w1ru-variant-id]');
    var buttons = root.querySelectorAll('[data-w1ru-variant]');
    var priceEl = root.querySelector('[data-w1ru-price]');
    var compareEl = root.querySelector('[data-w1ru-compare-price]');
    var saveEl = root.querySelector('[data-w1ru-save-badge]');
    var finalEl = root.querySelector('[data-w1ru-final]');
    var permodEl = root.querySelector('[data-w1ru-permod]');
    var includedEl = root.querySelector('[data-w1ru-included]');
    var bulletSets = root.querySelectorAll('[data-w1ru-bullet-set]');
    var labelEl = root.querySelector('[data-w1ru-variant-label]');
    var submitBtn = root.querySelector('[data-w1ru-submit]');
    var gallery = root.querySelector('[data-w1ru-gallery]');
    var soldOutText = root.getAttribute('data-sold-out-text') || 'Sold out';
    var addText = root.getAttribute('data-add-text') || 'Add to cart';
    var syncStickyBar = initStickyBar(root);

    function selectVariant(button) {
      if (!button) return;

      setActive(buttons, button, 'is-active');
      each(buttons, function (el) {
        el.setAttribute('aria-checked', el === button ? 'true' : 'false');
      });

      if (variantInput) variantInput.value = button.getAttribute('data-variant-id') || '';
      if (priceEl) priceEl.innerHTML = button.getAttribute('data-price') || priceEl.innerHTML;

      if (compareEl) {
        var compare = button.getAttribute('data-compare-price') || '';
        compareEl.innerHTML = compare;
        compareEl.hidden = compare === '';
      }

      if (saveEl) {
        var save = button.getAttribute('data-save-badge') || '';
        saveEl.innerHTML = save;
        saveEl.hidden = save === '';
      }

      if (finalEl) {
        var finalPrice = button.getAttribute('data-final') || '';
        finalEl.innerHTML = finalPrice;
        finalEl.hidden = finalPrice === '';
      }

      if (permodEl) {
        var perModule = button.getAttribute('data-permod') || '';
        permodEl.innerHTML = perModule;
        permodEl.hidden = perModule === '';
      }

      if (includedEl) {
        var included = button.getAttribute('data-included') || '';
        includedEl.innerHTML = included;
        includedEl.hidden = included === '';
      }

      if (bulletSets.length) {
        var setKey = button.getAttribute('data-bullet-set') || 'default';
        each(bulletSets, function (list) {
          list.hidden = list.getAttribute('data-w1ru-bullet-set') !== setKey;
        });
      }

      if (labelEl) labelEl.textContent = button.getAttribute('data-variant-title') || '';

      var available = button.getAttribute('data-available') === 'true';
      if (submitBtn) {
        submitBtn.disabled = !available;
        submitBtn.textContent = available ? addText : soldOutText;
      }
      if (variantInput) variantInput.disabled = !available;

      if (syncStickyBar) syncStickyBar(button, { available: available, text: available ? addText : soldOutText });

      if (gallery && gallery.w1ruShowMedia) {
        gallery.w1ruShowMedia(button.getAttribute('data-media-id'));
      }

      var url = button.getAttribute('data-variant-url');
      if (url && window.history && window.history.replaceState) {
        window.history.replaceState({}, '', url);
      }
    }

    each(buttons, function (button) {
      button.addEventListener('click', function () {
        if (button.hasAttribute('disabled')) return;
        selectVariant(button);
      });
      button.addEventListener('keydown', function (event) {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          button.click();
        }
      });
    });

    var qtyInput = root.querySelector('[data-w1ru-qty-input]');
    var minus = root.querySelector('[data-w1ru-qty-minus]');
    var plus = root.querySelector('[data-w1ru-qty-plus]');

    function stepQty(delta) {
      if (!qtyInput) return;
      var min = parseInt(qtyInput.getAttribute('min'), 10) || 1;
      var max = parseInt(qtyInput.getAttribute('max'), 10) || Infinity;
      var value = (parseInt(qtyInput.value, 10) || min) + delta;
      qtyInput.value = Math.min(max, Math.max(min, value));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (minus) minus.addEventListener('click', function () { stepQty(-1); });
    if (plus) plus.addEventListener('click', function () { stepQty(1); });

    var active = root.querySelector('[data-w1ru-variant].is-active');
    if (active) selectVariant(active);
  }

  /* ----------------------------------------------------------------------
     Generic tab/panel switcher — demand states and therapy modes.
     Panels carry their index in the attribute value so one set of tabs can
     drive several panel groups (copy column + media column).
     ---------------------------------------------------------------------- */
  function initSwitcher(root, tabSelector, panelAttr) {
    if (!markInit(root)) return;

    var tabs = root.querySelectorAll(tabSelector);
    var panels = root.querySelectorAll('[' + panelAttr + ']');
    if (!tabs.length || !panels.length) return;

    function activate(i) {
      each(tabs, function (tab, index) {
        tab.classList.toggle('is-active', index === i);
        tab.setAttribute('aria-selected', index === i ? 'true' : 'false');
      });
      each(panels, function (panel) {
        var showing = parseInt(panel.getAttribute(panelAttr), 10) === i;
        panel.hidden = !showing;
        if (!showing) {
          eachVideoIn(panel, function (video) {
            if (video.getAttribute('src') && !video.paused) video.pause();
          });
          return;
        }
        // Asked for here as well as by the observer, because this runs inside
        // the tap that chose the card and a browser holding muted clips back
        // is far likelier to allow one then. Only once the frame is on screen:
        // opening the page must not fetch a clip nobody has scrolled to.
        if (nearlyInView(panel)) eachVideoIn(panel, playVideo);
      });
    }

    // One state's heading wraps to two lines and the next one's does not, so
    // swapping them lifts or drops everything below the panel. Each group of
    // panels is held open to the tallest of its members.
    //
    // Measured one at a time with the rest hidden, all inside a single task, so
    // nothing is painted mid-measurement. Groups whose panels are already the
    // same height — the image column, where every panel fills the same frame —
    // are left alone rather than pinned to a height the stylesheet owns.
    function lockHeights() {
      var groups = [];
      each(panels, function (panel) {
        var group = null;
        for (var i = 0; i < groups.length; i++) {
          if (groups[i].parent === panel.parentElement) group = groups[i];
        }
        if (!group) {
          group = { parent: panel.parentElement, list: [] };
          groups.push(group);
        }
        group.list.push(panel);
      });

      groups.forEach(function (group) {
        if (!group.parent || group.list.length < 2) return;
        group.parent.style.minHeight = '';

        var was = group.list.map(function (panel) { return panel.hidden; });
        group.list.forEach(function (panel) { panel.hidden = true; });

        var heights = group.list.map(function (panel) {
          panel.hidden = false;
          var height = panel.getBoundingClientRect().height;
          panel.hidden = true;
          return height;
        });
        group.list.forEach(function (panel, i) { panel.hidden = was[i]; });

        var tallest = Math.max.apply(null, heights);
        var shortest = Math.min.apply(null, heights);
        if (tallest - shortest < 1) return;

        // The reset puts every box on border-box, so a min-height is a promise
        // about the border box and the parent's own padding eats into it.
        var style = window.getComputedStyle(group.parent);
        var chrome = 0;
        if (style.boxSizing === 'border-box') {
          chrome =
            (parseFloat(style.paddingTop) || 0) +
            (parseFloat(style.paddingBottom) || 0) +
            (parseFloat(style.borderTopWidth) || 0) +
            (parseFloat(style.borderBottomWidth) || 0);
        }
        group.parent.style.minHeight = Math.ceil(tallest + chrome) + 'px';
      });
    }

    each(tabs, function (tab, i) {
      tab.addEventListener('click', function () { activate(i); });
    });

    activate(0);
    lockHeights();

    // Width decides how the copy wraps, and a web font arriving late rewraps it
    // again. Nothing here observes its own output, so there is no loop to guard.
    var relockTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(relockTimer);
      relockTimer = window.setTimeout(lockHeights, 150);
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(lockHeights);
  }

  /* ----------------------------------------------------------------------
     Video

     Three clips weigh nine megabytes between them and not one of them is on
     the first screen, yet all three used to be fetched as the page opened —
     including the one in a hidden panel nobody had asked to see. So a clip's
     source waits in an attribute the browser does not act on until its frame
     is nearly in view.

     Playing is then asked for by hand rather than left to the autoplay
     attribute. WeChat's browser holds a muted clip back until its own bridge
     is up or the page has been touched, and a clip that starts in a hidden
     panel never gets a second chance on its own.
     ---------------------------------------------------------------------- */
  var videoObserver = null;

  // Shopify keeps the same clip at several sizes. A frame a few hundred pixels
  // wide has no use for the 720p one, and on the connection a phone is likely
  // to be on it is the difference between playing and buffering.
  function sourceFor(video) {
    var small = video.getAttribute('data-w1ru-src-sm');
    if (small && window.matchMedia && window.matchMedia('(max-width: 1024px)').matches) return small;
    return video.getAttribute('data-w1ru-src');
  }

  function readyVideo(video) {
    var src = sourceFor(video);
    if (!src || video.getAttribute('src') === src) return;
    video.setAttribute('src', src);
    video.load();
  }

  function playVideo(video) {
    if (!video) return;
    readyVideo(video);
    // A browser that is refusing on purpose says so through the promise, and
    // there is nothing useful to do about it beyond leaving the poster up.
    var playing = video.play();
    if (playing && playing.catch) playing.catch(function () {});
  }

  function eachVideoIn(el, fn) {
    if (!el) return;
    if (el.tagName === 'VIDEO') fn(el);
    each(el.querySelectorAll('video[data-w1ru-video]'), fn);
  }

  // Close enough to the fold that the clip has started by the time it is read,
  // far enough that nothing above it is competing for the connection.
  function nearlyInView(el) {
    var box = el.getBoundingClientRect();
    if (!box.width || !box.height) return false;
    var tall = window.innerHeight || document.documentElement.clientHeight || 0;
    return box.top < tall + 400 && box.bottom > -400;
  }

  function observeVideos(scope) {
    var targets = (scope || document).querySelectorAll('video[data-w1ru-video]:not([data-w1ru-watched])');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      each(targets, function (video) {
        video.setAttribute('data-w1ru-watched', 'true');
        playVideo(video);
      });
      return;
    }

    if (!videoObserver) {
      videoObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) playVideo(entry.target);
            else if (entry.target.getAttribute('src') && !entry.target.paused) entry.target.pause();
          });
        },
        { rootMargin: '400px 0px' }
      );
    }

    each(targets, function (video) {
      video.setAttribute('data-w1ru-watched', 'true');
      videoObserver.observe(video);
    });
  }

  // A second attempt at playing what has already been fetched — never a reason
  // to start fetching, which is what the observer is for.
  function nudgeVideos() {
    each(document.querySelectorAll('video[data-w1ru-video]'), function (video) {
      if (!video.getAttribute('src') || !video.paused) return;
      if (video.hidden || (video.closest && video.closest('[hidden]'))) return;
      playVideo(video);
    });
  }

  var awaitingPermission = false;

  function askAgainWhenAllowed() {
    if (awaitingPermission) return;
    awaitingPermission = true;

    // WeChat's own signal that a muted clip may now start.
    document.addEventListener('WeixinJSBridgeReady', nudgeVideos);
    each(['touchstart', 'click'], function (name) {
      document.addEventListener(name, nudgeVideos, { passive: true });
    });
    // A browser pauses what scrolls out of a background tab and offers nothing
    // to start it again.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') nudgeVideos();
    });
  }

  /* ----------------------------------------------------------------------
     Tech video carousel
     ---------------------------------------------------------------------- */
  function initTech(root) {
    if (!markInit(root)) return;

    var tabs = root.querySelectorAll('[data-w1ru-tech-tab]');
    var dots = root.querySelectorAll('[data-w1ru-tech-dot]');
    var panels = root.querySelectorAll('[data-w1ru-tech-panel]');
    var video = root.querySelector('[data-w1ru-tech-video]');
    if (!panels.length) return;

    var index = 0;
    var timer = null;
    var interval = parseInt(root.getAttribute('data-autoplay-interval'), 10) || 4500;
    var autoplayEnabled = root.getAttribute('data-autoplay') !== 'false';
    var handheld = window.matchMedia ? window.matchMedia('(max-width: 1024px)') : null;

    function render(i) {
      index = (i + panels.length) % panels.length;

      each(tabs, function (tab, n) {
        tab.classList.toggle('is-active', n === index);
        tab.setAttribute('aria-selected', n === index ? 'true' : 'false');
      });
      each(dots, function (dot, n) {
        dot.classList.toggle('is-active', n === index);
      });
      each(panels, function (panel) {
        panel.hidden = parseInt(panel.getAttribute('data-w1ru-tech-panel'), 10) !== index;
      });

      if (!video) return;

      var panel = panels[index];
      var src = panel.getAttribute('data-video-src') || '';
      var small = panel.getAttribute('data-video-src-sm') || '';
      var poster = panel.getAttribute('data-video-poster') || '';

      if (poster) video.setAttribute('poster', poster);

      if (src) {
        video.hidden = false;
        video.setAttribute('data-w1ru-src', src);
        if (small) video.setAttribute('data-w1ru-src-sm', small);
        else video.removeAttribute('data-w1ru-src-sm');
        // Until the stage has been scrolled to, a slide change only records
        // which clip belongs here; the observer fetches it on the way past.
        // After that there is no play button to fall back on, so every slide
        // change asks again rather than only the ones that swap the source.
        if (video.getAttribute('src')) playVideo(video);
      } else {
        video.removeAttribute('data-w1ru-src');
        video.removeAttribute('data-w1ru-src-sm');
        video.removeAttribute('src');
        video.hidden = true;
      }
    }

    function start() {
      if (!autoplayEnabled || panels.length < 2) return;
      // A phone shows one tab name at a time and moves on the arrows, so a
      // slide that changed by itself would be one nobody chose — and the clip
      // under it would never get far enough in to play, being reloaded every
      // few seconds. The desktop layout, with all the tabs on screen, keeps it.
      if (handheld && handheld.matches) return;
      stop();
      timer = window.setInterval(function () { render(index + 1); }, interval);
    }

    function stop() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }

    each(tabs, function (tab, i) {
      tab.addEventListener('click', function () { render(i); start(); });
    });
    each(dots, function (dot, i) {
      dot.addEventListener('click', function () { render(i); start(); });
    });

    var prev = root.querySelector('[data-w1ru-tech-prev]');
    var next = root.querySelector('[data-w1ru-tech-next]');
    if (prev) prev.addEventListener('click', function () { render(index - 1); start(); });
    if (next) next.addEventListener('click', function () { render(index + 1); start(); });

    // Turning the phone sideways can cross the breakpoint either way.
    if (handheld) {
      var onWidthChange = function () {
        if (handheld.matches) stop();
        else start();
      };
      if (handheld.addEventListener) handheld.addEventListener('change', onWidthChange);
      else if (handheld.addListener) handheld.addListener(onWidthChange);
    }

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);

    render(0);
    start();
  }

  /* ----------------------------------------------------------------------
     Photo lightbox — scenario cards
     ---------------------------------------------------------------------- */
  function initLightbox(root) {
    if (!markInit(root)) return;

    var triggers = root.querySelectorAll('[data-w1ru-zoom]');
    var dialog = root.querySelector('[data-w1ru-lightbox]');
    var image = root.querySelector('[data-w1ru-lightbox-img]');
    var close = root.querySelector('[data-w1ru-lightbox-close]');
    if (!triggers.length || !dialog || !image || !dialog.showModal) return;

    // A phone shows the picture no larger than the card did, and the tap would
    // only get in the way of scrolling past the grid. The query is read at
    // click time so rotating a tablet is respected.
    var wideEnough = window.matchMedia('(min-width: 769px)');

    each(triggers, function (trigger) {
      trigger.addEventListener('click', function () {
        if (!wideEnough.matches) return;
        // Dimensions before source, so the box is the shape of the photo that
        // is arriving rather than the shape of the one before it.
        var width = trigger.getAttribute('data-zoom-width');
        var height = trigger.getAttribute('data-zoom-height');
        if (width && height) {
          image.setAttribute('width', width);
          image.setAttribute('height', height);
        }
        image.setAttribute('alt', trigger.getAttribute('data-zoom-alt') || '');
        image.setAttribute('src', trigger.getAttribute('data-zoom-src') || '');
        dialog.showModal();
      });
    });

    if (close) close.addEventListener('click', function () { dialog.close(); });

    // The dialog fills the viewport so its backdrop never receives the click.
    // Anything landing on the dialog itself is beside the picture.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });

    // Dropping the source keeps a large file from sitting decoded in memory
    // once the reader has moved on.
    dialog.addEventListener('close', function () {
      image.removeAttribute('src');
    });
  }

  /* ----------------------------------------------------------------------
     Discount code — copy and confirm
     ---------------------------------------------------------------------- */

  // navigator.clipboard is absent over plain http and inside some embedded
  // views, and the theme editor's iframe refuses it outright. The old textarea
  // trick still works in all of them, and a code is short enough that a manual
  // selection is a fair last resort.
  function writeClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }

    return new Promise(function (resolve, reject) {
      var field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.top = '-2000px';
      document.body.appendChild(field);
      field.select();

      var copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (err) {
        copied = false;
      }

      document.body.removeChild(field);
      copied ? resolve() : reject(new Error('copy rejected'));
    });
  }

  /* ----------------------------------------------------------------------
     Rating badge — travel to the review list
     ---------------------------------------------------------------------- */

  function initReviewsJump(link) {
    if (!markInit(link)) return;

    link.addEventListener('click', function (event) {
      // Left alone, the anchor jumps: the list is thousands of pixels down and
      // arriving there without the page moving reads as a different page.
      event.preventDefault();

      var id = (link.getAttribute('href') || '').replace('#', '');
      var target = id && document.getElementById(id);

      // The list is an app block, so it is only here if the app is installed
      // and the block placed. With nowhere to go, going nowhere beats being
      // thrown to the top of the document.
      if (!target) return;

      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var OFFSET = 24;

      function scrolled() {
        return window.pageYOffset || document.documentElement.scrollTop || 0;
      }

      // The first travel is the one the eye follows. Corrections after that
      // have to be instant: a second smooth scroll aborts the first, and the
      // page then sits wherever the abort happened to be.
      function aim(smooth) {
        window.scrollTo({
          top: scrolled() + target.getBoundingClientRect().top - OFFSET,
          behavior: reduced || !smooth ? 'auto' : 'smooth',
        });
      }

      // Sections between here and the list paint as they are reached, so the
      // page is not long enough to bring the list to the top until they have.
      // Keep correcting until it is, or until the shopper takes the wheel.
      var done = false;
      var events = ['wheel', 'touchstart', 'keydown'];
      var timer;
      var watch;

      function release() {
        if (done) return;
        done = true;
        clearInterval(timer);
        if (watch) watch.disconnect();
        each(events, function (name) {
          window.removeEventListener(name, release);
        });
      }

      function closeEnough() {
        return Math.abs(target.getBoundingClientRect().top - OFFSET) < 8;
      }

      function correct() {
        if (done || closeEnough()) return release();
        aim(false);
      }

      each(events, function (name) {
        window.addEventListener(name, release, { passive: true });
      });

      // Give the smooth travel a head start. Correcting from the first frame
      // — including a ResizeObserver firing as it attaches — aborts it, and
      // the page then sits a few hundred pixels short.
      setTimeout(function () {
        if (done) return;
        if (window.ResizeObserver) {
          watch = new ResizeObserver(correct);
          watch.observe(document.documentElement);
        }
        timer = setInterval(correct, 300);
        correct();
      }, 700);

      setTimeout(release, 8000);

      aim(true);

      // Leaves the position in the address bar, so it survives a reload and the
      // back button returns to the buy box rather than the top of the page.
      if (window.history && history.replaceState) history.replaceState(null, '', '#' + id);
    });
  }

  function initPromoCopy(trigger) {
    if (!markInit(trigger)) return;

    var strip = trigger.closest('.w1ru-promo');
    var flash = strip && strip.querySelector('[data-w1ru-promo-flash]');
    if (!flash) return;

    var line = flash.querySelector('[data-w1ru-promo-flash-text]') || flash;
    var confirmed = line.textContent;
    var timer;

    function say(state, text) {
      clearTimeout(timer);
      line.textContent = text;
      strip.setAttribute('data-flash', state);
      timer = setTimeout(function () {
        strip.removeAttribute('data-flash');
      }, 3000);
    }

    trigger.addEventListener('click', function () {
      writeClipboard(trigger.getAttribute('data-code') || '').then(
        function () { say('copied', confirmed); },
        // A refused write must not be congratulated. The code is on the button
        // that was just pressed, so there is nothing to reproduce here.
        function () {
          say('manual', (window.copyStrings && window.copyStrings.copyFailed) || 'Copy the code above by hand');
        }
      );
    });
  }

  /* ----------------------------------------------------------------------
     FAQ accordion
     ---------------------------------------------------------------------- */
  function initFaq(root) {
    if (!markInit(root)) return;

    each(root.querySelectorAll('[data-w1ru-faq-q]'), function (button) {
      button.addEventListener('click', function () {
        var item = button.closest('[data-w1ru-faq-item]');
        if (!item) return;
        var open = item.classList.toggle('is-open');
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ----------------------------------------------------------------------
     Specification table — sideways scroll hints
     ---------------------------------------------------------------------- */
  function initSpecs(root) {
    if (!markInit(root)) return;

    var scroller = root.querySelector('[data-w1ru-specs-scroll]');
    if (!scroller) return;

    var prev = root.querySelector('[data-w1ru-specs-prev]');
    var next = root.querySelector('[data-w1ru-specs-next]');
    var rail = root.querySelector('.w1ru-specs-hints');

    // Fractional widths leave a sub-pixel of travel at either end, which would
    // otherwise read as "there is more" forever.
    function sync() {
      var slack = scroller.scrollWidth - scroller.clientWidth;
      root.classList.toggle('has-more-before', slack > 1 && scroller.scrollLeft > 1);
      root.classList.toggle('has-more-after', slack > 1 && scroller.scrollLeft < slack - 1);
    }

    // Fourteen rows are taller than a phone, so arrows left at one height are
    // arrows nobody reading the other end can see. They ride down the table
    // with the page, stopping short of either edge so they always look like
    // they belong to the table.
    function place() {
      if (!rail) return;
      var box = root.getBoundingClientRect();
      var tall = window.innerHeight || document.documentElement.clientHeight || 0;
      var eye = tall * 0.46 - box.top;
      var lowest = box.height - 48;
      if (lowest < 14) return;
      rail.style.transform = 'translateY(' + Math.round(Math.max(14, Math.min(lowest, eye))) + 'px)';
    }

    function move(direction) {
      var step = direction * scroller.clientWidth * 0.8;
      if (scroller.scrollBy) scroller.scrollBy({ left: step, behavior: 'smooth' });
      else scroller.scrollLeft += step;
    }

    if (prev) prev.addEventListener('click', function () { move(-1); });
    if (next) next.addEventListener('click', function () { move(1); });

    var pending = false;
    function onPageScroll() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        place();
      });
    }

    scroller.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('scroll', onPageScroll, { passive: true });
    window.addEventListener('resize', function () {
      sync();
      place();
    });

    sync();
    place();
    // A web font arriving late rewraps the cells, which changes both how wide
    // the table is and how tall.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        sync();
        place();
      });
    }
  }

  /* ----------------------------------------------------------------------
     Therapy modes — card rail
     ---------------------------------------------------------------------- */
  // The rail scrolls on its own; the arrows move it one card at a time and
  // go quiet at either end. Fractional widths leave a sub-pixel of travel,
  // which would otherwise keep the last arrow lit forever.
  function initModeDeck(root) {
    if (!markInit(root)) return;

    var rail = root.querySelector('[data-w1ru-mode-rail]');
    if (!rail) return;
    var prev = root.querySelector('[data-w1ru-mode-prev]');
    var next = root.querySelector('[data-w1ru-mode-next]');

    function step() {
      var card = rail.firstElementChild;
      if (!card) return rail.clientWidth;
      var gap = parseFloat(window.getComputedStyle(rail).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function sync() {
      var slack = rail.scrollWidth - rail.clientWidth;
      if (prev) prev.disabled = rail.scrollLeft <= 1;
      if (next) next.disabled = rail.scrollLeft >= slack - 1;
    }

    function move(direction) {
      var left = direction * step();
      if (rail.scrollBy) rail.scrollBy({ left: left, behavior: 'smooth' });
      else rail.scrollLeft += left;
    }

    if (prev) prev.addEventListener('click', function () { move(-1); });
    if (next) next.addEventListener('click', function () { move(1); });
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  /* ----------------------------------------------------------------------
     Daily scenarios — the gliding row
     ---------------------------------------------------------------------- */
  // The stylesheet's keyframe glides the row for a reader without this file.
  // Here the track is placed by hand instead, so the row can also be dragged,
  // swiped and stepped. The row is the same set of cards laid several times
  // over, and the track's offset is kept inside one set's width — wrapping it
  // by a whole set moves the track onto an identical copy, which is what makes
  // the row endless both ways without anyone seeing the join. Left alone the
  // row glides at the pace the settings ask for; touched, it follows the
  // pointer, settles on the nearest card when let go, and takes the glide up
  // again a few seconds later.
  function initScenStrip(strip) {
    if (!markInit(strip)) return;

    var track = strip.querySelector('[data-w1ru-scen-track]');
    var set = track && track.firstElementChild;
    if (!set || !set.firstElementChild) return;

    var section = strip.closest ? strip.closest('[data-w1ru-scenarios]') : null;
    var scope = section || strip.parentNode;
    var prev = scope.querySelector('[data-w1ru-scen-prev]');
    var next = scope.querySelector('[data-w1ru-scen-next]');

    var query = window.matchMedia ? window.matchMedia.bind(window) : null;
    var reduced = query ? query('(prefers-reduced-motion: reduce)') : null;
    var canHover = query ? query('(hover: hover)') : null;
    var loop = parseFloat(strip.getAttribute('data-loop')) || 40;
    var forward = strip.getAttribute('data-direction') === 'right' ? -1 : 1;
    var pauseOnHover = strip.hasAttribute('data-pause-hover');

    var RESUME_AFTER = 4000; // ms of being left alone before the glide returns
    var SETTLE_MS = 480;     // easing onto a card
    var FLING_MS = 150;      // how far ahead a release's speed is projected
    var SLACK = 2;           // cards the track can be pushed past its band, either way
    var DRAG_START = 4;      // px before a press counts as a drag

    var setWidth = 0;   // one set of cards, trailing gap included
    var step = 0;       // one card and its gap
    var base = 0;       // where the band the offset lives in starts
    var offset = 0;     // how far the track has travelled; the transform is its negative
    var glides = !(reduced && reduced.matches);
    var hovered = false;
    var onScreen = true;
    var resumeAt = 0;
    var settle = null;  // { from, to, start } while easing onto a card
    var drag = null;    // { id, startX, startOffset, lastX, lastTime, velocity, moved }
    var swallowClick = false;
    var lastFrame = 0;

    strip.classList.add('is-driven');

    function place() {
      track.style.transform = 'translate3d(' + (-offset) + 'px, 0, 0)';
    }

    // Back into the band, by whole sets only, so the picture does not change.
    function wrap(value) {
      if (!setWidth) return value;
      return base + ((((value - base) % setWidth) + setWidth) % setWidth);
    }

    function nearest(value) {
      return step ? Math.round(value / step) * step : value;
    }

    // The band starts a whole number of sets in, far enough that a push of
    // SLACK cards backwards still has track under it; and the track is made
    // long enough — with copies of its last set, which carries no editor
    // attributes — that a push forwards, plus the window itself, does too.
    // The section lays enough sets for a 2560px window at desktop widths; this
    // catches narrower cards on a phone and anything wider than that.
    function measure() {
      var before = setWidth;
      setWidth = set.getBoundingClientRect().width;
      if (!setWidth) return;
      var gap = parseFloat(window.getComputedStyle(set).columnGap) || 0;
      step = set.firstElementChild.getBoundingClientRect().width + gap;
      base = Math.ceil((SLACK * step) / setWidth) * setWidth;

      var needed = base + setWidth + SLACK * step + strip.clientWidth;
      var guard = 0;
      while (track.getBoundingClientRect().width < needed && guard++ < 12) {
        var copy = track.lastElementChild.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        each(copy.querySelectorAll('a, button'), function (el) { el.setAttribute('tabindex', '-1'); });
        track.appendChild(copy);
      }

      // Cards resized under the row: keep the same card in view.
      offset = wrap(before ? (offset / before) * setWidth : base);
      place();
    }

    function rest() {
      resumeAt = performance.now() + RESUME_AFTER;
    }

    // Ease onto `target`, which is measured from where the offset is now. The
    // offset is wrapped first and the target carried along by the same whole
    // sets, then held within SLACK cards, so however fast the arrows are hit
    // the track never runs out from under the window.
    function settleTo(target) {
      var wrapped = wrap(offset);
      target += wrapped - offset;
      offset = wrapped;
      target = Math.max(offset - SLACK * step, Math.min(offset + SLACK * step, target));
      settle = { from: offset, to: nearest(target), start: performance.now() };
      rest();
    }

    function frame(now) {
      if (!strip.isConnected) return;
      window.requestAnimationFrame(frame);
      var dt = lastFrame ? Math.min(64, now - lastFrame) : 0;
      lastFrame = now;
      if (!setWidth || drag) return;

      if (settle) {
        var t = Math.min(1, (now - settle.start) / SETTLE_MS);
        var eased = 1 - Math.pow(1 - t, 3);
        offset = settle.from + (settle.to - settle.from) * eased;
        if (t >= 1) {
          settle = null;
          offset = wrap(offset);
        }
        place();
        return;
      }

      if (!glides || hovered || !onScreen || now < resumeAt) return;
      offset = wrap(offset + (forward * setWidth * dt) / (loop * 1000));
      place();
    }

    function onDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (drag) return;
      settle = null;
      offset = wrap(offset);
      drag = {
        id: event.pointerId,
        startX: event.clientX,
        startOffset: offset,
        lastX: event.clientX,
        lastTime: event.timeStamp,
        velocity: 0,
        moved: false
      };
      if (strip.setPointerCapture) {
        try { strip.setPointerCapture(event.pointerId); } catch (err) { /* already released */ }
      }
    }

    function onMove(event) {
      if (!drag || event.pointerId !== drag.id) return;
      var dx = event.clientX - drag.startX;
      if (!drag.moved) {
        if (Math.abs(dx) < DRAG_START) return;
        drag.moved = true;
        strip.classList.add('is-dragging');
      }
      var dt = Math.max(1, event.timeStamp - drag.lastTime);
      // Smoothed a little, so one jittery last sample does not decide the fling.
      drag.velocity = drag.velocity * 0.6 + ((event.clientX - drag.lastX) / dt) * 0.4;
      drag.lastX = event.clientX;
      drag.lastTime = event.timeStamp;
      offset = wrap(drag.startOffset - dx);
      place();
      if (event.cancelable) event.preventDefault();
    }

    function onUp(event) {
      if (!drag || event.pointerId !== drag.id) return;
      var moved = drag.moved;
      var velocity = drag.velocity;
      // A release long after the last move is a stop, not a throw.
      if (event.timeStamp - drag.lastTime > 80) velocity = 0;
      drag = null;
      strip.classList.remove('is-dragging');
      if (strip.releasePointerCapture) {
        try { strip.releasePointerCapture(event.pointerId); } catch (err) { /* already released */ }
      }
      if (!moved) return;
      // The pointer moving right carries the track right, so the offset falls.
      settleTo(offset - velocity * FLING_MS);
      swallowClick = true;
      window.setTimeout(function () { swallowClick = false; }, 0);
    }

    strip.addEventListener('pointerdown', onDown);
    strip.addEventListener('pointermove', onMove);
    strip.addEventListener('pointerup', onUp);
    strip.addEventListener('pointercancel', onUp);
    strip.addEventListener('lostpointercapture', onUp);

    // A tile that is a link would otherwise open at the end of a drag that
    // happened to finish over it, and a browser would pick the link up and
    // drag it rather than let the row move.
    strip.addEventListener('click', function (event) {
      if (!swallowClick) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
    strip.addEventListener('dragstart', function (event) { event.preventDefault(); });

    // Counted from the card the row is already heading for, so a second click
    // before the first has landed goes one further rather than being lost.
    function anchor() {
      return settle ? settle.to : nearest(offset);
    }

    if (prev) prev.addEventListener('click', function () { settleTo(anchor() - step); });
    if (next) next.addEventListener('click', function () { settleTo(anchor() + step); });

    // Pausing under the pointer is for pointers that hover; a finger would
    // leave the row stopped after every tap.
    if (pauseOnHover && canHover && canHover.matches) {
      strip.addEventListener('mouseenter', function () { hovered = true; });
      strip.addEventListener('mouseleave', function () { hovered = false; });
    }

    if (reduced && reduced.addEventListener) {
      reduced.addEventListener('change', function (event) { glides = !event.matches; });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[entries.length - 1].isIntersecting;
      }).observe(strip);
    }

    if (window.ResizeObserver) {
      new ResizeObserver(measure).observe(strip);
    } else {
      window.addEventListener('resize', measure);
    }

    measure();
    window.requestAnimationFrame(frame);
  }

  /* ----------------------------------------------------------------------
     Comparison table — raised card column
     ---------------------------------------------------------------------- */
  // The stylesheet places the card from the column-width settings, which is
  // right until a column is left on "auto". Reading the header cell's real
  // box settles it. It is measured against the stage the card is positioned
  // in — the box the table itself sits in, which on a phone scrolls inside
  // the wrapper carrying the card along — so the reveal's transform cancels
  // out and a scroll changes nothing: the card no longer has to be chased
  // after the column, which is what made it drift.
  function initCompareCard(wrap) {
    if (!markInit(wrap)) return;

    var frame = wrap.querySelector('.w1ru-table-frame');
    var stage = wrap.querySelector('.w1ru-table-stage') || wrap;
    var head = wrap.querySelector('th.w1ru-col-hi');
    if (!frame || !head) return;

    function place() {
      var box = stage.getBoundingClientRect();
      var cell = head.getBoundingClientRect();
      if (!cell.width) return;
      wrap.style.setProperty('--w1ru-cmp-hi-left', (cell.left - box.left) + 'px');
      wrap.style.setProperty('--w1ru-cmp-hi-width', cell.width + 'px');
      // Where the header ends, so the card can carry the header's colour
      // from its own top edge down to that line.
      wrap.style.setProperty('--w1ru-cmp-head-bottom', (cell.bottom - box.top) + 'px');
      wrap.classList.add('w1ru-table-wrap--head-measured');
    }

    var pending = false;
    function queue() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        place();
      });
    }

    if (window.ResizeObserver) {
      new ResizeObserver(queue).observe(frame);
    } else {
      window.addEventListener('resize', queue);
    }
    // A web font arriving late rewraps the header, which moves the column.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(queue);

    place();
  }

  /* ----------------------------------------------------------------------
     Scroll reveal
     ---------------------------------------------------------------------- */
  var revealObserver = null;

  function observeReveals(scope) {
    var targets = (scope || document).querySelectorAll('[data-w1ru-reveal]:not([data-w1ru-observed])');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      each(targets, function (el) { el.classList.add('is-visible'); });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.08 }
      );
    }

    each(targets, function (el) {
      el.setAttribute('data-w1ru-observed', 'true');
      el.classList.add('w1ru-reveal');
      revealObserver.observe(el);
    });
  }

  /* ----------------------------------------------------------------------
     Bootstrap
     ---------------------------------------------------------------------- */
  function init(scope) {
    var root = scope || document;

    each(root.querySelectorAll('[data-w1ru-gallery]'), initGallery);
    each(root.querySelectorAll('[data-w1ru-buybox]'), initBuybox);
    each(root.querySelectorAll('[data-w1ru-states]'), function (el) {
      initSwitcher(el, '[data-w1ru-state-btn]', 'data-w1ru-state-panel');
    });
    each(root.querySelectorAll('[data-w1ru-modes]'), function (el) {
      initSwitcher(el, '[data-w1ru-mode-tab]', 'data-w1ru-mode-panel');
    });
    each(root.querySelectorAll('[data-w1ru-mode-deck]'), initModeDeck);
    each(root.querySelectorAll('[data-w1ru-wear]'), function (el) {
      initSwitcher(el, '[data-w1ru-wear-btn]', 'data-w1ru-wear-panel');
    });
    each(root.querySelectorAll('[data-w1ru-reviews-jump]'), initReviewsJump);
    each(root.querySelectorAll('[data-w1ru-promo-copy]'), initPromoCopy);
    each(root.querySelectorAll('[data-w1ru-tech]'), initTech);
    each(root.querySelectorAll('[data-w1ru-scenarios]'), initLightbox);
    each(root.querySelectorAll('[data-w1ru-scen-strip]'), initScenStrip);
    each(root.querySelectorAll('[data-w1ru-specs]'), initSpecs);
    each(root.querySelectorAll('[data-w1ru-cmp-card]'), initCompareCard);
    each(root.querySelectorAll('[data-w1ru-faq]'), initFaq);

    observeReveals(root);
    // After initTech, which is what puts a source on the shared stage video.
    observeVideos(root);
    askAgainWhenAllowed();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.W1RegenUltra = { init: init };
})();
